<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  slaExceededTotal: {
    type: Number,
    default: 0,
  },
  slaExceededByStage: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['view-kanban']);

const { t } = useI18n();

const stageEntries = computed(() =>
  Object.entries(props.slaExceededByStage || {}).sort(([, a], [, b]) => b - a)
);

const maxCount = computed(() =>
  stageEntries.value.reduce((max, [, count]) => Math.max(max, count), 0)
);

const hasData = computed(() => props.slaExceededTotal > 0);
</script>

<template>
  <div class="p-5 rounded-lg border border-n-weak bg-n-solid-2">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <div
          class="flex items-center justify-center w-9 h-9 rounded-lg bg-n-ruby-3"
        >
          <Icon icon="i-lucide-alarm-clock-off" class="text-n-ruby-11" />
        </div>
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('PIPELINE.DASHBOARD.FORGOTTEN_LEADS.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('PIPELINE.DASHBOARD.FORGOTTEN_LEADS.SUBTITLE') }}
          </p>
        </div>
      </div>
      <span
        v-if="hasData"
        class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-ruby-3 text-n-ruby-11 border border-n-ruby-6"
      >
        {{ slaExceededTotal }}
      </span>
    </div>

    <div v-if="!hasData" class="flex flex-col items-center gap-2 py-6">
      <Icon icon="i-lucide-check-circle" class="size-8 text-n-teal-11" />
      <p class="text-xs text-n-slate-11">
        {{ t('PIPELINE.DASHBOARD.FORGOTTEN_LEADS.EMPTY') }}
      </p>
    </div>

    <div v-else class="space-y-2">
      <div
        v-for="[stage, count] in stageEntries"
        :key="stage"
        class="flex items-center gap-3"
      >
        <span
          class="w-28 truncate text-xs text-n-slate-12 font-medium"
          :title="stage"
        >
          {{ stage }}
        </span>
        <div class="flex-1 h-2 rounded-full bg-n-alpha-2 overflow-hidden">
          <div
            class="h-full bg-n-ruby-11"
            :style="{ width: `${(count / maxCount) * 100}%` }"
          />
        </div>
        <span class="w-8 text-right text-xs font-semibold text-n-ruby-11">
          {{ count }}
        </span>
      </div>

      <button
        type="button"
        class="mt-3 w-full py-2 text-xs font-medium text-n-brand hover:text-n-brand/80 transition-colors"
        @click="emit('view-kanban')"
      >
        {{ t('PIPELINE.DASHBOARD.FORGOTTEN_LEADS.VIEW_ALL') }}
      </button>
    </div>
  </div>
</template>
